import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowLeft, BookOpen } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getBlogArticle } from '../lib/api';
import Navbar from '../components/Navbar';
import { Footer } from '../components/Footer';

export default function BlogArticlePage() {
  const { id } = useParams();
  const { language } = useLanguage();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticle();
  }, [id]);

  const loadArticle = async () => {
    try {
      const data = await getBlogArticle(id);
      setArticle(data);
    } catch (err) {
      console.error('Failed to load article:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { 
      year: 'numeric', month: 'long', day: 'numeric' 
    });
  };

  const renderMarkdown = (text) => {
    if (!text) return '';
    return text
      .replace(/^### (.*$)/gm, '<h3 class="text-xl font-bold text-white mt-8 mb-3">$1</h3>')
      .replace(/^## (.*$)/gm, '<h2 class="text-2xl font-bold text-white mt-10 mb-4">$1</h2>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[#4A90D9] hover:underline">$1</a>')
      .replace(/^- (.*$)/gm, '<li class="ml-4 text-white/70 mb-1">$1</li>')
      .replace(/^(\d+)\. (.*$)/gm, '<li class="ml-4 text-white/70 mb-1">$1. $2</li>')
      .replace(/\n\n/g, '</p><p class="text-white/70 leading-relaxed mb-4">')
      .replace(/\n/g, '<br/>');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1521]">
        <Navbar />
        <div className="pt-32 text-center">
          <BookOpen size={48} className="mx-auto text-white/20 mb-4 animate-pulse" />
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#0B1521]">
        <Navbar />
        <div className="pt-32 text-center">
          <BookOpen size={48} className="mx-auto text-white/20 mb-4" />
          <p className="text-white/60 text-lg">{language === 'fr' ? 'Article non trouvé' : 'Article not found'}</p>
          <Link to="/blog" className="inline-flex items-center gap-2 mt-6 text-[#4A90D9] hover:underline">
            <ArrowLeft size={16} />
            {language === 'fr' ? 'Retour au blog' : 'Back to blog'}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const title = language === 'fr' ? article.title_fr : article.title_en;
  const content = language === 'fr' ? article.content_fr : article.content_en;

  return (
    <div className="min-h-screen bg-[#0B1521]" data-testid="blog-article-page">
      <Navbar />

      {/* Hero */}
      {article.image_url && (
        <div className="w-full h-[300px] sm:h-[400px] relative">
          <img src={article.image_url} alt={title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1521] via-[#0B1521]/60 to-transparent" />
        </div>
      )}

      <section className={`${article.image_url ? '-mt-20' : 'pt-32'} pb-20 px-4 relative z-10`}>
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            {/* Back link */}
            <Link to="/blog" className="inline-flex items-center gap-2 text-[#4A90D9] hover:underline text-sm mb-8" data-testid="back-to-blog">
              <ArrowLeft size={16} />
              {language === 'fr' ? 'Retour au blog' : 'Back to blog'}
            </Link>

            {/* Title */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6" data-testid="article-title">
              {title}
            </h1>

            {/* Meta */}
            <div className="flex items-center gap-6 text-sm text-white/40 mb-10 pb-8 border-b border-white/10">
              <span className="flex items-center gap-2">
                <Calendar size={14} />
                {formatDate(article.created_at)}
              </span>
              <span className="flex items-center gap-2">
                <User size={14} />
                {article.author}
              </span>
            </div>

            {/* Content */}
            <div 
              className="prose-custom text-white/70 leading-relaxed text-base sm:text-lg"
              dangerouslySetInnerHTML={{ __html: `<p class="text-white/70 leading-relaxed mb-4">${renderMarkdown(content)}</p>` }}
              data-testid="article-content"
            />

            {/* CTA */}
            <div className="mt-16 p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-center">
              <h3 className="text-xl font-bold text-white mb-3">
                {language === 'fr' ? 'Vous avez un projet en tête ?' : 'Have a project in mind?'}
              </h3>
              <p className="text-white/60 mb-6">
                {language === 'fr' 
                  ? "Contactez-nous pour discuter de votre idée et obtenir un devis gratuit."
                  : 'Contact us to discuss your idea and get a free quote.'}
              </p>
              <Link
                to="/payment"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#4A90D9] text-white rounded-lg font-semibold hover:bg-[#5BA0E9] transition-colors"
                data-testid="article-cta"
              >
                {language === 'fr' ? 'Demander un devis' : 'Request a quote'}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
